import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, imgUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PenTool, Palette, PersonStanding, ArrowRight } from "lucide-react";

const TOOLS = [
  { to: "/studio/sketch", icon: PenTool, title: "Sketch to Render", desc: "Turn a design sketch into a photorealistic product render." },
  { to: "/studio/materials", icon: Palette, title: "Apply Materials & Prints", desc: "Change fabrics, prints and colorways from your library." },
  { to: "/studio/tryon", icon: PersonStanding, title: "Virtual Model Try-On", desc: "Visualize outfits on models in any setting or location." },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [designs, setDesigns] = useState([]);

  useEffect(() => {
    api.get("/designs").then((r) => setDesigns(r.data.slice(0, 8))).catch(() => {});
  }, []);

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Workspace</p>
      <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight" data-testid="dashboard-greeting">
        Good to see you, {user?.name?.split(" ")[0]}.
      </h1>
      <p className="text-zinc-500 mt-2 text-sm">Start a new project or continue where your team left off.</p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
        {TOOLS.map((t) => (
          <Link key={t.to} to={t.to} data-testid={`tool-card-${t.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="group border border-zinc-200 bg-white rounded-lg p-7 hover:shadow-sm hover:-translate-y-1 transition-transform duration-200">
            <t.icon size={22} />
            <h3 className="font-heading font-semibold mt-5">{t.title}</h3>
            <p className="text-sm text-zinc-500 mt-1.5 leading-relaxed">{t.desc}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium">
              Open tool <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        ))}
      </div>

      <div className="mt-14 flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold tracking-tight">Recent team designs</h2>
        <Link to="/designs" data-testid="dashboard-view-all-designs" className="text-sm font-medium hover:text-zinc-500 transition-colors">View all →</Link>
      </div>
      {designs.length === 0 ? (
        <div className="mt-6 border-2 border-dashed border-zinc-200 rounded-lg p-14 text-center text-zinc-400 text-sm" data-testid="dashboard-empty-state">
          No designs yet — start with a sketch.
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {designs.map((d) => (
            <Link key={d.id} to={`/designs/${d.id}`} data-testid={`recent-design-${d.id}`} className="group border border-zinc-200 bg-white rounded-lg overflow-hidden hover:shadow-sm transition-shadow">
              <img src={imgUrl(d.cover_image)} alt={d.title} className="w-full h-44 object-cover group-hover:scale-[1.02] transition-transform duration-300" />
              <div className="p-3">
                <p className="text-sm font-medium truncate">{d.product_title || d.title}</p>
                <p className="text-xs text-zinc-400">{d.owner_name} · {d.versions.length} iteration{d.versions.length > 1 ? "s" : ""}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
